import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { ChevronRight, MapPin, Calendar, Share2, Phone, ArrowRight, CheckCircle2, Zap, Award, Users, Building2, ClipboardCheck, Wrench, HardHat, Sparkles, ShieldCheck, Clock, HelpCircle } from "lucide-react";
import CTASection from "../../components/public/CTASection";
import { useSettings } from "../../lib/useSettings";

export function RealisationsListPage() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [audience, setAudience] = useState("all"); // all | particulier | pro

  useEffect(() => { api.get("/realizations").then(r => setItems(r.data)); }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map(i => i.category).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(r => {
      if (cat !== "all" && r.category !== cat) return false;
      if (audience === "particulier" && r.client_type && r.client_type !== "particulier") return false;
      if (audience === "pro" && r.client_type === "particulier") return false;
      return true;
    });
  }, [items, cat, audience]);

  return (
    <>
      <title>Nos réalisations — Installations & chantiers | Kloub'Air Plomberie</title>
      <meta name="description" content="Découvrez nos dernières réalisations de climatisation et plomberie dans les Bouches-du-Rhône." />

      <section className="pt-14 pb-10 bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-white/60 mb-4 flex items-center gap-2">
            <Link to="/">Accueil</Link> <ChevronRight className="w-3 h-3" /> Réalisations
          </nav>
          <h1 className="font-display text-4xl md:text-6xl font-black">Nos réalisations</h1>
          <p className="mt-3 text-white/75 max-w-2xl">Installations, dépannages et rénovations dans un rayon de 40 km autour de Grans.</p>
        </div>
      </section>

      {/* FILTRES — scrollables horizontalement sur mobile */}
      <section className="sticky top-[68px] z-30 bg-white/90 backdrop-blur border-b border-[hsl(var(--border))] py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-2.5">
          {/* Audience */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] flex-shrink-0">Pour :</span>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="audience-filters">
              {[
                { v: "all", label: "Tous", Icon: Users },
                { v: "particulier", label: "Particuliers", Icon: Users },
                { v: "pro", label: "Pros & Entreprises", Icon: Building2 },
              ].map(a => (
                <button key={a.v} onClick={() => setAudience(a.v)}
                  className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-bold whitespace-nowrap transition-all flex-shrink-0 ${audience === a.v ? "bg-[color:var(--brand-navy)] text-white shadow" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/70"}`}
                  data-testid={`audience-${a.v}`}
                ><a.Icon className="w-3 h-3" />{a.label}</button>
              ))}
            </div>
          </div>
          {/* Catégories */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] flex-shrink-0">Type :</span>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="category-filters">
              {categories.map(c => (
                <button key={c} onClick={() => setCat(c)}
                  className={`px-3 h-8 rounded-full text-xs font-bold whitespace-nowrap transition-all flex-shrink-0 ${cat === c ? "bg-[color:var(--brand-sky)] text-white shadow" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/70"}`}
                  data-testid={`cat-${c}`}
                >{c === "all" ? "Tout" : c}</button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-5 flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
            <span><strong className="text-[color:var(--brand-navy)]">{filtered.length}</strong> réalisation{filtered.length > 1 ? "s" : ""}</span>
            {cat !== "all" || audience !== "all" ? (
              <button onClick={() => { setCat("all"); setAudience("all"); }} className="text-[color:var(--brand-sky)] font-semibold hover:underline">Réinitialiser</button>
            ) : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filtered.map((r) => (
              <Link key={r.id} to={`/realisations/${r.slug}`} className="group rounded-2xl overflow-hidden aspect-[4/5] relative bg-[color:var(--brand-navy)] shadow-soft hover:shadow-hover transition-shadow" data-testid={`realisation-list-${r.slug}`}>
                {r.images?.[0] && <img src={r.images[0]} alt={r.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2 flex-wrap">
                  {r.category && <span className="px-2.5 py-1 rounded-full bg-white/95 text-[color:var(--brand-navy)] text-[10px] font-bold uppercase tracking-wider">{r.category}</span>}
                  {r.brand && <span className="px-2.5 py-1 rounded-full bg-[color:var(--brand-sky)] text-white text-[10px] font-bold uppercase">{r.brand}</span>}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <div className="text-xs flex items-center gap-2 opacity-90 mb-1">
                    {r.zone && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.zone}</span>}
                    {r.intervention_type && <span className="text-[color:var(--brand-sky)] font-semibold">· {r.intervention_type}</span>}
                  </div>
                  <h3 className="font-display text-lg font-bold leading-tight line-clamp-2">{r.title}</h3>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-16 text-center text-sm text-[hsl(var(--muted-foreground))]">
              Aucune réalisation ne correspond à vos filtres.
              <button onClick={() => { setCat("all"); setAudience("all"); }} className="block mx-auto mt-3 text-[color:var(--brand-sky)] font-semibold hover:underline">Voir toutes les réalisations</button>
            </div>
          )}
        </div>
      </section>
      <CTASection />
    </>
  );
}

export function RealisationDetailPage() {
  const { slug } = useParams();
  const { settings: s } = useSettings();
  const [r, setR] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/realizations/${slug}`).then(res => setR(res.data)).catch(() => setR(false));
  }, [slug]);

  useEffect(() => {
    if (!r) return;
    api.get("/realizations").then(res => {
      const others = (res.data || []).filter(x => x.id !== r.id);
      // Priority: same category, then same zone, then any
      const sameCat = others.filter(x => x.category === r.category).slice(0, 3);
      const fill = sameCat.length < 3 ? others.filter(x => !sameCat.includes(x)).slice(0, 3 - sameCat.length) : [];
      setRelated([...sameCat, ...fill].slice(0, 3));
    });
  }, [r]);

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: r.title, text: r.meta_description || r.description, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert("Lien copié !");
      }
    } catch {}
  };

  if (r === false) return <div className="py-24 text-center">Réalisation introuvable.</div>;
  if (!r) return <div className="py-24 text-center">Chargement...</div>;

  // Schema.org JSON-LD — CreativeWork project page for rich SERP display
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": r.title,
    "description": r.meta_description || r.description,
    "datePublished": r.date || r.created_at,
    "image": r.images || [],
    "author": { "@type": "Organization", "name": "KLOUB'AIR PLOMBERIE", "url": "https://kap-clim.fr" },
    "about": r.category,
    "locationCreated": r.zone ? { "@type": "Place", "name": r.zone, "address": { "@type": "PostalAddress", "addressLocality": r.zone, "postalCode": r.postal_code } } : undefined,
  };

  const clientLabel = { particulier: "Particulier", professionnel: "Professionnel", entreprise: "Entreprise", collectivite: "Collectivité" }[r.client_type] || r.client_type;
  const phone = s?.phone;

  return (
    <>
      <title>{r.meta_title || r.title}</title>
      <meta name="description" content={r.meta_description} />
      <meta property="og:title" content={r.meta_title || r.title} />
      <meta property="og:description" content={r.meta_description} />
      {r.images?.[0] && <meta property="og:image" content={r.images[0]} />}
      <meta property="og:type" content="article" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <section className="pt-14 pb-10 bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-white/60 mb-4 flex items-center gap-2 flex-wrap">
            <Link to="/">Accueil</Link><ChevronRight className="w-3 h-3" />
            <Link to="/realisations">Réalisations</Link><ChevronRight className="w-3 h-3" />
            <span>{r.category}</span>
          </nav>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-white/15 text-xs font-bold uppercase tracking-wider">{r.category}</span>
              {r.intervention_type && <span className="px-3 py-1 rounded-full bg-[color:var(--brand-sky)] text-white text-xs font-bold uppercase tracking-wider">{r.intervention_type}</span>}
              {r.brand && <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-bold uppercase tracking-wider">{r.brand}</span>}
            </div>
            <button onClick={share} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-xs font-bold transition-colors" data-testid="real-share-btn">
              <Share2 className="w-3.5 h-3.5" /> Partager
            </button>
          </div>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-black leading-[1.05]">{r.title}</h1>
          <div className="mt-4 flex items-center gap-5 text-sm text-white/80 flex-wrap">
            {r.zone && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" /> {r.zone}{r.postal_code && ` · ${r.postal_code}`}</span>}
            {r.date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" /> {new Date(r.date).toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}</span>}
            {clientLabel && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" /> {clientLabel}</span>}
          </div>
        </div>
      </section>

      {/* Stats chips — preuves chiffrées */}
      {(r.duration || r.surface || r.budget_range || r.benefits?.length > 0) && (
        <section className="bg-white border-b border-[hsl(var(--border))]">
          <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex gap-3 overflow-x-auto no-scrollbar">
            {r.duration && <Chip Icon={Zap} label="Durée" value={r.duration} />}
            {r.surface && <Chip Icon={Award} label="Surface" value={r.surface} />}
            {r.benefits?.length > 0 && <Chip Icon={CheckCircle2} label="Bénéfices" value={`${r.benefits.length} points`} />}
            {r.budget_range && <Chip Icon={Award} label="Budget" value={r.budget_range} />}
          </div>
        </section>
      )}

      {/* Images gallery — mosaïque premium */}
      {r.images?.length > 0 && (
        <section className="py-10 bg-[hsl(var(--muted))]/40">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            {r.images.length === 1 ? (
              <img src={r.images[0]} alt={r.title} className="rounded-3xl w-full aspect-video object-cover shadow-xl" />
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                <img src={r.images[0]} alt={r.title} className="rounded-3xl w-full aspect-[4/3] object-cover md:row-span-2 md:aspect-auto shadow-xl" />
                {r.images.slice(1, 5).map((img, i) => (
                  <img key={i} src={img} alt={`${r.title} ${i + 2}`} className="rounded-2xl w-full aspect-[4/3] object-cover shadow-lg" loading="lazy" />
                ))}
              </div>
            )}
            {r.images.length > 5 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                {r.images.slice(5).map((img, i) => (
                  <img key={i} src={img} alt={`${r.title} ${i + 6}`} className="rounded-2xl w-full aspect-square object-cover shadow-md" loading="lazy" />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Main body */}
      <section className="py-14">
        <div className="max-w-5xl mx-auto px-4 md:px-8 grid lg:grid-cols-[2fr_1fr] gap-10">
          <div className="space-y-8">
            {r.objective && (
              <div>
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold mb-2"><span>🎯</span> Objectif du projet</div>
                <p className="text-lg leading-relaxed text-[hsl(var(--foreground))]/85 whitespace-pre-line">{r.objective}</p>
              </div>
            )}
            {r.solution && (
              <div>
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold mb-2"><span>⚙</span> Solution apportée par KLOUB'AIR</div>
                <p className="text-lg leading-relaxed text-[hsl(var(--foreground))]/85 whitespace-pre-line">{r.solution}</p>
              </div>
            )}
            {r.description && (
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold mb-2">Récit du chantier</div>
                {/^\s*<(p|h[1-6]|ul|ol|blockquote|div|img|figure|iframe|strong|em)[\s>]/i.test(r.description) ? (
                  <div className="kap-article-body" dangerouslySetInnerHTML={{ __html: r.description }} />
                ) : (
                  <div className="text-base leading-relaxed text-[hsl(var(--foreground))]/85 whitespace-pre-line">{r.description}</div>
                )}
              </div>
            )}
            {r.youtube_url && (
              <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                <iframe src={`https://www.youtube.com/embed/${r.youtube_url}`} className="w-full h-full" title={r.title} allowFullScreen />
              </div>
            )}
          </div>

          {/* Sidebar — sticky on desktop */}
          <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            {r.benefits?.length > 0 && (
              <div className="rounded-2xl border border-[hsl(var(--border))] p-5 bg-gradient-to-br from-[color:var(--brand-sky)]/5 to-white">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[color:var(--brand-sky)] flex items-center justify-center"><Award className="w-4 h-4 text-white" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold">Bénéfices client</div>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))]">Ce qu'ils gagnent avec nous</div>
                  </div>
                </div>
                <ul className="space-y-3">
                  {r.benefits.map((b, i) => (
                    <li key={i} className="flex gap-3 text-sm text-[hsl(var(--foreground))]/85 group">
                      <span className="w-7 h-7 rounded-full bg-white border-2 border-[color:var(--brand-sky)] text-[color:var(--brand-sky)] text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[color:var(--brand-sky)] group-hover:text-white transition-colors">
                        {i + 1}
                      </span>
                      <span className="leading-relaxed pt-0.5">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(r.brand || r.model || r.budget_range || r.duration || r.surface) && (
              <div className="rounded-2xl border border-[hsl(var(--border))] p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold mb-3">Fiche technique</div>
                <dl className="space-y-2 text-sm">
                  {r.brand && <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">Marque</dt><dd className="font-semibold">{r.brand}</dd></div>}
                  {r.model && <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">Modèle</dt><dd className="font-semibold text-right max-w-[60%] truncate">{r.model}</dd></div>}
                  {r.surface && <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">Surface</dt><dd className="font-semibold">{r.surface}</dd></div>}
                  {r.duration && <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">Durée</dt><dd className="font-semibold">{r.duration}</dd></div>}
                  {r.budget_range && <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">Budget</dt><dd className="font-semibold">{r.budget_range}</dd></div>}
                </dl>
              </div>
            )}

            {/* Main CTA */}
            <div className="rounded-2xl p-5 bg-[color:var(--brand-navy)] text-white">
              <div className="font-display font-bold text-lg mb-1">Un projet similaire ?</div>
              <div className="text-sm text-white/80 mb-4">Devis gratuit, sans engagement, sous 24h.</div>
              <Link to="/devis" className="inline-flex items-center gap-1.5 w-full justify-center h-11 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 font-bold transition-all" data-testid="real-cta-devis">
                Demander un devis <ArrowRight className="w-4 h-4" />
              </Link>
              {phone && (
                <a href={`tel:${phone.replace(/\s/g, "")}`} className="mt-2 inline-flex items-center gap-1.5 w-full justify-center h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 font-bold transition-all">
                  <Phone className="w-4 h-4" /> {phone}
                </a>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* Process — chronologie du chantier */}
      <section className="py-12 bg-[hsl(var(--muted))]/30 border-t border-[hsl(var(--border))]">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold mb-2">Notre méthodologie</div>
          <h2 className="font-display text-2xl md:text-3xl font-black text-[color:var(--brand-navy)] mb-6">Comment s'est déroulé ce projet ?</h2>
          <div className="grid md:grid-cols-4 gap-3 md:gap-4">
            {[
              { Icon: ClipboardCheck, step: "01", title: "Diagnostic", text: "Étude sur site, prise de mesures, analyse des contraintes techniques" },
              { Icon: Wrench, step: "02", title: "Devis détaillé", text: "Proposition sous 24h, matériel premium, transparence totale" },
              { Icon: HardHat, step: "03", title: "Installation", text: r.duration ? `Chantier réalisé en ${r.duration} par notre équipe dédiée` : "Installation soignée par nos techniciens certifiés" },
              { Icon: Sparkles, step: "04", title: "Mise en service", text: "Tests, formation du client, remise de tous les documents et garanties" },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl bg-white border border-[hsl(var(--border))] p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[color:var(--brand-sky)]/10 flex items-center justify-center">
                    <s.Icon className="w-5 h-5 text-[color:var(--brand-sky)]" />
                  </div>
                  <span className="font-display text-2xl font-black text-[color:var(--brand-navy)]/10">{s.step}</span>
                </div>
                <h3 className="font-display font-bold text-[color:var(--brand-navy)] mb-1">{s.title}</h3>
                <p className="text-xs text-[hsl(var(--foreground))]/70 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagements KLOUB'AIR */}
      <section className="py-12 bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { Icon: ShieldCheck, title: "Garantie décennale", text: "Tous nos chantiers couverts par notre assurance professionnelle." },
              { Icon: Award, title: "Matériel premium", text: "Uniquement des grandes marques (DAIKIN, MITSUBISHI, HITACHI)." },
              { Icon: Clock, title: "SAV réactif", text: "Intervention en 48h maxi en cas de besoin, 6 jours sur 7." },
            ].map((e, i) => (
              <div key={i} className="flex gap-4 items-start p-5 rounded-2xl bg-white/5 border border-white/10">
                <div className="w-11 h-11 rounded-xl bg-[color:var(--brand-sky)] flex items-center justify-center flex-shrink-0">
                  <e.Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-display font-bold mb-1">{e.title}</div>
                  <div className="text-xs text-white/70 leading-relaxed">{e.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ rapide — rich snippet boost */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold mb-2">Les questions fréquentes</div>
          <h2 className="font-display text-2xl md:text-3xl font-black text-[color:var(--brand-navy)] mb-5">À propos de ce type de chantier</h2>
          <div className="space-y-2.5">
            {[
              {
                q: `Combien coûte un projet ${r.category?.toLowerCase() || "similaire"} à ${r.zone || "votre adresse"} ?`,
                a: r.budget_range
                  ? `Ce chantier spécifique s'est inscrit dans une gamme ${r.budget_range}. Chaque projet étant unique, nous établissons un devis personnalisé et gratuit après visite sur site sous 24h.`
                  : "Chaque projet est unique. Nous réalisons un devis personnalisé et gratuit après visite sur site, remis sous 24h.",
              },
              {
                q: `Quel est le délai d'intervention${r.zone ? ` à ${r.zone}` : ""} ?`,
                a: `KLOUB'AIR intervient dans un rayon de 40 km autour de Grans. Un devis est établi sous 24h, et l'installation est programmée selon vos disponibilités${r.duration ? ` (compter environ ${r.duration} de chantier comme dans cette réalisation)` : ""}.`,
              },
              {
                q: "Quelles garanties sont incluses ?",
                a: `Tous nos chantiers bénéficient de la garantie décennale, de la garantie constructeur sur le matériel (souvent 5 ans minimum${r.brand ? ` pour la marque ${r.brand}` : ""}) et d'une garantie de pose d'un an.`,
              },
              {
                q: "Intervenez-vous aussi pour les professionnels ?",
                a: "Oui — nous accompagnons particuliers, professionnels, commerces, bureaux, restaurants et collectivités. Nous adaptons notre offre et nos délais à votre activité.",
              },
            ].map((f, i) => (
              <details key={i} className="group rounded-2xl bg-white border border-[hsl(var(--border))] p-5 open:shadow-md transition-shadow" data-testid={`real-faq-${i}`}>
                <summary className="cursor-pointer font-display font-bold text-[color:var(--brand-navy)] flex items-start gap-3 list-none">
                  <HelpCircle className="w-5 h-5 mt-0.5 text-[color:var(--brand-sky)] flex-shrink-0" />
                  <span className="flex-1">{f.q}</span>
                  <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] transition-transform group-open:rotate-90" />
                </summary>
                <div className="mt-3 pl-8 text-sm text-[hsl(var(--foreground))]/80 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related realizations */}
      {related.length > 0 && (
        <section className="py-12 bg-[hsl(var(--muted))]/40 border-t border-[hsl(var(--border))]">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-6 gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold mb-1">Dans la même lignée</div>
                <h2 className="font-display text-2xl md:text-3xl font-black text-[color:var(--brand-navy)]">Autres réalisations {r.category}</h2>
              </div>
              <Link to="/realisations" className="text-sm font-semibold text-[color:var(--brand-sky)] hover:underline hidden md:inline">Voir toutes →</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map(rl => (
                <Link key={rl.id} to={`/realisations/${rl.slug}`} className="group rounded-2xl overflow-hidden aspect-[4/5] relative bg-[color:var(--brand-navy)] shadow-soft hover:shadow-hover transition-shadow">
                  {rl.images?.[0] && <img src={rl.images[0]} alt={rl.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute top-3 left-3"><span className="px-2 py-0.5 rounded-full bg-white/95 text-[color:var(--brand-navy)] text-[10px] font-bold uppercase">{rl.category}</span></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    {rl.zone && <div className="text-[10px] flex items-center gap-1 opacity-80"><MapPin className="w-3 h-3" /> {rl.zone}</div>}
                    <h3 className="font-display text-base font-bold mt-1 line-clamp-2">{rl.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sticky mobile action bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[hsl(var(--border))] px-3 py-2.5 flex gap-2 shadow-2xl" data-testid="real-sticky-mobile-cta">
        {phone && (
          <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-[color:var(--brand-navy)] text-white font-bold text-sm">
            <Phone className="w-4 h-4" /> Appeler
          </a>
        )}
        <Link to="/devis" className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-[color:var(--brand-red)] text-white font-bold text-sm">
          Devis <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <CTASection />
    </>
  );
}

function Chip({ Icon, label, value }) {
  return (
    <div className="inline-flex items-center gap-2 flex-shrink-0 px-3.5 py-2 rounded-full bg-[color:var(--brand-sky)]/10 border border-[color:var(--brand-sky)]/20 text-[color:var(--brand-navy)] text-xs font-semibold">
      <Icon className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" />
      <span className="text-[hsl(var(--muted-foreground))]">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
